// Realtime device events over the Signaling WSS room broadcast (W3/APP-01).
//
// Spec: APP 16 §2.2（未讀紅點 = push + WSS room 廣播）/ §5.2（前景即時：room
// subscribe → 新事件即時插入時間軸 + 紅點）、APP 17 §4.1（前景即時來源）、
// APP 00 §9.4（subscribe room 級整批 + 冪等/refcount）。
//
// Wire frame (v3-dev 2026-06-12 實測定版，與 device/specs/03 §7.1 一致):
//   {"type":"event","device_id":"emu-emugw-emugw-cam-001",
//    "event_id":"0x1100008041000001",
//    "payload":{"label":"person","confidence":0.91,"sub_id":"0x01"},
//    "timestamp":1781194505}
// 注意：
//   - `event_id` 是 capability-encoded 型別 hex（與 REST 明細的 `event_id` 同名
//     同值），**非唯一鍵**。REST 的 `event_type` 則是 ch19 語意 enum（"motion"…，
//     restful 00 ch19 read-side 映射）——**不是** hex，去重/型別判定都不可用它
//     （W2-03）；
//   - `timestamp` 是「秒」（REST event_ts 為 µs）；
//   - frame **沒有 correlation_id** → 與 REST 明細的去重只能用
//     device + event_ts(µs) + `event_id` 型別 hex 複合 key（eventKey；spec 17
//     §4.5）。REST 回查（對帳）後同 key 的即時項由 REST 權威資料取代
//     （mergeTimeline 的 rest-wins 規則）。

import { signaling, type SignalFrame } from '../../lib/ws';
import { clearAllSnoozes } from '../devices/snooze';
import { clearHomeAwayMode } from '../home/homeAwayMode';
import { clearGeofencePresence } from '../home/geofencePresence';
import { compareRowsNewestFirst, type EventItem, type TimelineRow } from './eventsLib';

export interface LiveEvent {
  deviceId: string;
  ev: EventItem;
  /** 門鈴這一通的編號（signaling panelRingID：eid，沒有就 device_id:秒；docs/panel SD §7 末） */
  ringId?: string;
}

/** Current device state returned by command:subscribe. This snapshot closes the
 * "device online before the client was authorised/subscribed" race; subsequent
 * changes continue to arrive as presence events. */
export interface LiveDeviceStatus {
  deviceId: string;
  status: string;
}

/** 秒/毫秒/微秒 → 微秒（v3-dev frame 帶秒；REST 帶 µs；防衛性容兩者）。 */
export function toMicros(ts: number): number {
  if (!Number.isFinite(ts) || ts <= 0) return 0;
  if (ts < 1e11) return Math.round(ts * 1_000_000); // seconds (until year ~5138)
  if (ts < 1e14) return Math.round(ts * 1_000); // milliseconds
  return Math.round(ts); // already µs
}

/** 解析 room 廣播的 type:'event' frame；缺必要欄位回 null（防壞 frame 入軸）。 */
export function parseEventFrame(frame: SignalFrame): LiveEvent | null {
  if (frame.type !== 'event') return null;
  const deviceId = typeof frame.device_id === 'string' ? frame.device_id : '';
  const eventId = typeof frame.event_id === 'string' ? frame.event_id : '';
  const tsUs = toMicros(typeof frame.timestamp === 'number' ? frame.timestamp : NaN);
  if (!deviceId || !eventId || tsUs <= 0) return null;
  const corr = typeof frame.correlation_id === 'string' && frame.correlation_id ? frame.correlation_id : undefined;
  const channel = typeof frame.channel === 'number' && Number.isInteger(frame.channel) && frame.channel > 0 ? frame.channel : undefined;
  const eid = typeof frame.eid === 'string' && frame.eid ? frame.eid : '';
  const rawTs = typeof frame.timestamp === 'number' ? frame.timestamp : 0;
  return {
    deviceId,
    ringId: eid || (rawTs ? `${deviceId}:${rawTs}` : undefined),
    ev: {
      event_id: eventId,
      // wire 無語意 event_type；型別判定走 event_id（clusterOfEvent，17 §4.2），
      // 此欄僅為 EventItem 形狀佔位（REST 對帳後由語意 enum 取代）。
      event_type: eventId,
      event_ts: tsUs,
      ...(channel ? { channel } : {}),
      ...(corr ? { correlation_id: corr } : {}),
      payload: (frame.payload ?? undefined) as EventItem['payload'],
    },
  };
}

/**
 * REST/WSS 共同可得的事件識別 key（frame 無 correlation_id，故唯一性取
 * device + µs 時戳 + 型別 hex；同秒同型別的極端重複會合併——可接受，
 * 視覺上完全相同的列，且對帳後以 REST 為準）。
 *
 * W2-03：hex 取 `event_id`（REST/WSS 同名同值，spec 17 §4.5 的「型別 hex」）。
 * 舊版誤用 `event_type`——REST 端已映射為 ch19 語意 enum（"motion"…），與
 * live 列的 hex 永不相等 → REST 對帳取代失效、重連後同事件重複兩列。
 */
export function eventKey(deviceId: string, ev: EventItem): string {
  return `${deviceId}|${ev.event_ts}|${ev.event_id}`;
}

/**
 * 合併 REST 分頁列與即時 live 列（單一排序路徑，APP-03 comparator）：
 * REST 權威 — live 列若與任一 REST 列同 key 即剔除（對帳取代）；其餘插入後
 * 整體 newest-first。
 */
export function mergeTimeline(rest: TimelineRow[], live: TimelineRow[]): TimelineRow[] {
  const seen = new Set(rest.map((r) => eventKey(r.deviceId, r.ev)));
  const out = rest.slice();
  for (const r of live) {
    const k = eventKey(r.deviceId, r.ev);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(r);
  }
  out.sort(compareRowsNewestFirst);
  return out;
}

/** 訂閱解析後的即時事件（壞 frame 已濾掉）。回傳解除函數。 */
export function onLiveEvent(cb: (e: LiveEvent) => void): () => void {
  return signaling.on('event', (frame) => {
    const parsed = parseEventFrame(frame);
    if (parsed) cb(parsed);
  });
}

const deviceStatusSubs = new Set<(status: LiveDeviceStatus) => void>();

/** Subscribe to the authoritative point-in-time status carried by subscribe responses. */
export function onLiveDeviceStatus(cb: (status: LiveDeviceStatus) => void): () => void {
  deviceStatusSubs.add(cb);
  return () => {
    deviceStatusSubs.delete(cb);
  };
}

function publishSubscribeStatus(deviceId: string, frame: SignalFrame): void {
  const status = frame.payload?.device_status;
  if (typeof status !== 'string' || !status) return;
  for (const fn of deviceStatusSubs) fn({ deviceId, status });
}

// ---- 未讀紅點 store（16 §2.2；v1 本地、不跨裝置同步：16 §5.3/§9-G5）----

type Listener = () => void;

let unread = 0;
const unreadSubs = new Set<Listener>();

function notifyUnread(): void {
  for (const fn of unreadSubs) fn();
}

export const unreadEvents = {
  get(): number {
    return unread;
  },
  bump(): void {
    unread += 1;
    notifyUnread();
  },
  clear(): void {
    if (unread === 0) return;
    unread = 0;
    notifyUnread();
  },
  subscribe(fn: Listener): () => void {
    unreadSubs.add(fn);
    return () => {
      unreadSubs.delete(fn);
    };
  },
};

// ---- per-item 已讀狀態 store（APP#10；16 §5.3/§9-G5：v1 本地、不跨裝置同步）----
//
// 以 eventKey（device|µs|event_id 型別 hex）記已讀；localStorage 持久化(survive
// refresh)。登出（signaling state 'closed'）連同 unread/desiredRooms 一起清空 —
// 共用瀏覽器避免跨帳號已讀外洩。集合上限夾制(保留最近 READ_CAP 筆)防無限長。
// 與粗粒度 nav 紅點（unreadEvents）正交:前者 per-item、後者「離開事件頁後又來新事件」。

const READ_LS_KEY = 'ls.events.read.v1';
const READ_CAP = 2000;

function loadReadKeys(): Set<string> {
  try {
    const raw = globalThis.localStorage?.getItem(READ_LS_KEY);
    if (!raw) return new Set();
    const arr: unknown = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr.filter((x): x is string => typeof x === 'string')) : new Set();
  } catch {
    return new Set(); // 壞 JSON / SSR → 空集合
  }
}

let readKeys: Set<string> = loadReadKeys();
let readVersion = 0;
const readSubs = new Set<Listener>();

function persistReadKeys(): void {
  try {
    if (readKeys.size > READ_CAP) readKeys = new Set([...readKeys].slice(-READ_CAP));
    globalThis.localStorage?.setItem(READ_LS_KEY, JSON.stringify([...readKeys]));
  } catch {
    /* quota / SSR — 退化為記憶體內狀態 */
  }
}

function notifyRead(): void {
  readVersion += 1;
  for (const fn of readSubs) fn();
}

export const readEvents = {
  isRead(key: string): boolean {
    return readKeys.has(key);
  },
  /** useSyncExternalStore 快照:每次變更遞增,供 React 重繪。 */
  getVersion(): number {
    return readVersion;
  },
  /** 標一或多筆為已讀（冪等;無變化不通知）。 */
  markRead(...keys: string[]): void {
    let changed = false;
    for (const k of keys) {
      if (k && !readKeys.has(k)) {
        readKeys.add(k);
        changed = true;
      }
    }
    if (changed) {
      persistReadKeys();
      notifyRead();
    }
  },
  /** 給定 keys 中尚未讀的數量。 */
  unreadCount(keys: readonly string[]): number {
    let n = 0;
    for (const k of keys) if (!readKeys.has(k)) n += 1;
    return n;
  },
  /** 全清(登出時呼叫;避免共用瀏覽器跨帳號外洩)。 */
  clear(): void {
    readKeys = new Set();
    try {
      globalThis.localStorage?.removeItem(READ_LS_KEY);
    } catch {
      /* SSR */
    }
    notifyRead();
  },
  subscribe(fn: Listener): () => void {
    readSubs.add(fn);
    return () => {
      readSubs.delete(fn);
    };
  },
};

// ---- room 訂閱 registry（events/devices 頁的可見裝置 rooms）----
//
// 頁面以 ensureEventRooms(ids) 宣告「這些 device 的事件我要即時收」；登入
// session 期間維持訂閱（離頁保留 → 未讀紅點持續累計，16 §2.2）。重連後
// （server 房籍隨連線消失）於 'open' 重訂全部；登出（signaling close →
// state 'closed'）清空。ws.ts subscribe 已冪等+refcount（APP 00 §9.4），
// 與 live 頁/設定頁共 room 不互踩。

const desiredRooms = new Set<string>();
let registryWired = false;

function subscribeDesiredRoom(deviceId: string): void {
  void signaling.subscribe(deviceId).then(
    (frame) => publishSubscribeStatus(deviceId, frame),
    () => {
      /* reconnect / a later ensureEventRooms call retries */
    },
  );
}

function wireRegistry(): void {
  if (registryWired) return;
  registryWired = true;
  signaling.on('state', (s) => {
    if (s === 'open') {
      for (const id of desiredRooms) subscribeDesiredRoom(id);
    } else if (s === 'closed') {
      // 使用者登出（AuthContext signaling.close()）：清空，避免下個帳號重訂舊 rooms
      desiredRooms.clear();
      unreadEvents.clear();
      readEvents.clear(); // 已讀集合一併清(共用瀏覽器跨帳號隔離,APP#10)
      clearAllSnoozes(); // snooze 截止時戳一併清(共用瀏覽器跨帳號隔離,APP#19)
      clearHomeAwayMode(); // Home/Away 安全模式一併清(共用瀏覽器跨帳號隔離,APP#44)
      clearGeofencePresence(); // APP#26:清本機住家座標/規則 id；無 watcher 即不再 fire。
    }
  });
  // 裝置被移除授權 → 不再追蹤其 room（清單刷新由 useDeviceList 處理）
  signaling.on('device_revoked', (info) => {
    if (info.deviceId) desiredRooms.delete(info.deviceId);
  });
}

/** 宣告要即時接收事件的裝置 rooms（冪等；自動連線、open 時補訂）。 */
export function ensureEventRooms(deviceIds: string[]): void {
  wireRegistry();
  for (const id of deviceIds) {
    if (!id || desiredRooms.has(id)) continue;
    desiredRooms.add(id);
    if (signaling.getState() === 'open') subscribeDesiredRoom(id);
  }
  signaling.connect();
}

/** 測試用：重置模組狀態。 */
export function __resetLiveEventsForTest(): void {
  desiredRooms.clear();
  deviceStatusSubs.clear();
  unread = 0;
  unreadSubs.clear();
  readKeys = new Set();
  readVersion = 0;
  readSubs.clear();
  try {
    globalThis.localStorage?.removeItem(READ_LS_KEY);
  } catch {
    /* SSR */
  }
}
